import "./global.css";
import React, { lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Claim } from "./drops/eighth_seal/Claim"; // scaffolded screen
import { Sidebar } from "./components/Sidebar";
import { XOPublicShellWithSuspense } from "./components/XOPublicShell";
import { XOPublicQuietLoading } from "./components/XOPublicQuietLoading";
import { XOCatchAllRoute } from "./components/XOCatchAllRoute";
import {
  isMessageBottleV2Path,
  isPublicCinematicShellPath,
} from "./navigation/navConfig";
import { isCallDedicatedProductRootHost } from "./lib/call_product_hosts";
import { SharedContinuityRoomHostApp } from "./components/SharedContinuityRoomHostApp";
import { isSharedContinuityRoomProductHost } from "./lib/shared_continuity_room_hosts";
import {
  isRegistrarAllowedHost,
  registrarRedirectForHost,
  resolveRootLandingExperience,
} from "./lib/host_root_landing";
import {
  resolveBrandedDomainSurface,
  resolveBrandedRedirectTarget,
} from "./lib/branded_domain_surface";
import { BrandedDomainExternalRedirect } from "./components/BrandedDomainExternalRedirect";
import { Navigate } from "react-router-dom";
import { HostSurfaceAutoRoute } from "./components/HostSurfaceAutoRoute";
import {
  AppErrorBoundary,
  CallRouteErrorBoundary,
  XoProofRouteErrorBoundary,
} from "./components/AppErrorBoundary";
import {
  isXoDemoSurfacePath,
  XODemoSurfaceShell,
} from "./components/XODemoSurfaceShell";
import { CallRouteShell } from "./components/call/CallRouteShell";
import { CanonicallsRouteGate } from "./components/canonicalls/CanonicallsRouteGate";
import {
  isCanonicallsHost,
  shouldUseCanonicallsShell,
} from "./lib/canonicallsHost";
import { isXOCenterHost } from "./lib/xo_center_host";
import { isXOCenterVisitorShellPath } from "./lib/xo_center_visibility";
import { developmentPreviewDestination } from "./lib/developmentPreviewRoute";

const XOReserve = lazy(() => import("./pages/XOReserve"));
const XOnvergence = lazy(() => import("./pages/XOnvergence"));
const XOTerminal = lazy(() => import("./pages/XOTerminal"));
const SignalPassPage = lazy(() => import("./signal-pass/SignalPassPage"));
const CallV1 = lazy(() => import("./pages/CallV1"));
const CallLanding = lazy(() => import("./pages/CallLanding"));
const XOCallContinuityDemoLaunch = lazy(
  () => import("./pages/XOCallContinuityDemoLaunch"),
);
const CallRoomAudioDemo = lazy(() => import("./pages/CallRoomAudioDemo"));
const SignalsDropPage = lazy(() => import("./drops/signals/Drop"));
const XOPitchPage = lazy(() => import("./pages/XOPitchPage"));
const Moments21XoLanding = lazy(() => import("./pages/Moments21XoLanding"));
const MyHarborPage = lazy(() => import("./pages/MyHarborPage"));
const HarborWalkPage = lazy(() => import("./pages/HarborWalkPage"));
const HarborDistrictPage = lazy(() => import("./pages/HarborDistrictPage"));
const HarborSealRoomPage = lazy(() => import("./pages/HarborSealRoomPage"));
const HarborProvenancePage = lazy(() => import("./pages/HarborProvenancePage"));
const HarborPhysicalPortalMarkPage = lazy(
  () => import("./pages/HarborPhysicalPortalMarkPage"),
);
const HarborPassageCeremonyPage = lazy(
  () => import("./pages/HarborPassageCeremonyPage"),
);
const FoundryCollectionScarcityPage = lazy(
  () => import("./pages/FoundryCollectionScarcityPage"),
);
const FoundryCareReviewDeskPage = lazy(
  () => import("./pages/FoundryCareReviewDeskPage"),
);
const FoundrySecureElementProvisioningPage = lazy(
  () => import("./pages/FoundrySecureElementProvisioningPage"),
);
const FoundryNfcRehearsalConsolePage = lazy(
  () => import("./pages/FoundryNfcRehearsalConsolePage"),
);
const FoundryNfcPilotMatrixPage = lazy(
  () => import("./pages/FoundryNfcPilotMatrixPage"),
);
const FoundryPhysicalPilotRunPage = lazy(
  () => import("./pages/FoundryPhysicalPilotRunPage"),
);
const FoundryPilotEvidenceCapsulePage = lazy(
  () => import("./pages/FoundryPilotEvidenceCapsulePage"),
);
const FoundryPilotPromotionReviewPage = lazy(
  () => import("./pages/FoundryPilotPromotionReviewPage"),
);
const HarborCareReceiptPage = lazy(
  () => import("./pages/HarborCareReceiptPage"),
);
const HarborCareProofPage = lazy(() => import("./pages/HarborCareProofPage"));
const HarborCareClosurePage = lazy(
  () => import("./pages/HarborCareClosurePage"),
);
const HarborCareJourneyPage = lazy(
  () => import("./pages/HarborCareJourneyPage"),
);
const HarborKeeperAuthorityProofPage = lazy(
  () => import("./pages/HarborKeeperAuthorityProofPage"),
);
const HarborKeeperLineageProofPage = lazy(
  () => import("./pages/HarborKeeperLineageProofPage"),
);
const HarborKeeperHandoffProofPage = lazy(
  () => import("./pages/HarborKeeperHandoffProofPage"),
);
const HarborPermanentArtifactPortalPage = lazy(
  () => import("./pages/HarborPermanentArtifactPortalPage"),
);
const HarborArtifactMarkBindingPage = lazy(
  () => import("./pages/HarborArtifactMarkBindingPage"),
);
const HarborSecureTapPresencePage = lazy(
  () => import("./pages/HarborSecureTapPresencePage"),
);
const HarborSecureElementLineagePage = lazy(
  () => import("./pages/HarborSecureElementLineagePage"),
);
const HarborLivingArtifactPassportPage = lazy(
  () => import("./pages/HarborLivingArtifactPassportPage"),
);
const HarborFreshPresenceCeremonyPage = lazy(
  () => import("./pages/HarborFreshPresenceCeremonyPage"),
);
const CompanionPresenceReceiverPage = lazy(
  () => import("./pages/CompanionPresenceReceiverPage"),
);
const XOPhysicalTrustHandbookPage = lazy(
  () => import("./pages/XOPhysicalTrustHandbookPage"),
);
const MomentsHarborArrivalPage = lazy(
  () => import("./pages/MomentsHarborArrivalPage"),
);
const MomentsAnchorSealCardPage = lazy(
  () => import("./pages/MomentsAnchorSealCardPage"),
);
const AetherHandlePage = lazy(() => import("./pages/aether/AetherHandlePage"));
const AetherRoutePage = lazy(() => import("./pages/aether/AetherRoutePage"));
const AetherIntentPage = lazy(() => import("./pages/aether/AetherIntentPage"));
const AetherRegistrarListPage = lazy(
  () => import("./pages/aether/AetherRegistrarListPage"),
);
const AetherRegistrarEditPage = lazy(
  () => import("./pages/aether/AetherRegistrarEditPage"),
);
const ProofViewerPage = lazy(() => import("./pages/ProofViewerPage"));
const HandoffPage = lazy(() => import("./pages/handoff/HandoffPage"));
const AetherSecureLanding = lazy(() => import("./pages/AetherSecureLanding"));
const BridgeSurface = lazy(() => import("./pages/BridgeSurface"));
const VaultSurface = lazy(() => import("./pages/VaultSurface"));
const ProposalLifecyclePreview = lazy(
  () => import("./pages/ProposalLifecyclePreview"),
);
const XOMissionControl = lazy(() => import("./pages/XOMissionControl"));
const XOProofPage = lazy(() => import("./pages/XOProofPage"));
const XOProofCallPage = lazy(() => import("./pages/XOProofCallPage"));
const XOProofAetherPage = lazy(() => import("./pages/XOProofAetherPage"));
const XOProofMaterialPage = lazy(() => import("./pages/XOProofMaterialPage"));
const XOProofArtifactPage = lazy(() => import("./pages/XOProofArtifactPage"));
const TrustContinuumFrame = lazy(() => import("./pages/TrustContinuumFrame"));
const XOContinuityScene = lazy(() => import("./pages/XOContinuityScene"));
const XOContinuitySession = lazy(() => import("./pages/XOContinuitySession"));
const XOSharedContinuityRoom = lazy(
  () => import("./pages/XOSharedContinuityRoom"),
);
const XOPrimerPage = lazy(() => import("./pages/XOPrimerPage"));
const PrimerControlRoom = lazy(() => import("./pages/PrimerControlRoom"));
const XOCenterPage = lazy(() => import("./pages/XOCenterPage"));
const XOCenterManifestPage = lazy(() => import("./pages/XOCenterManifestPage"));
const PrimerLiveDemo = lazy(() => import("./pages/PrimerLiveDemo"));
const XOPrimerWhatXoIsPage = lazy(() => import("./pages/XOPrimerWhatXoIsPage"));
const XOPrimerWhySystemsFailPage = lazy(
  () => import("./pages/XOPrimerWhySystemsFailPage"),
);
const XOPrimerTrustContinuumPage = lazy(
  () => import("./pages/XOPrimerTrustContinuumPage"),
);
const XOPrimerContinuitySurfacesPage = lazy(
  () => import("./pages/XOPrimerContinuitySurfacesPage"),
);
const XOPrimerDefenseLayerPage = lazy(
  () => import("./pages/XOPrimerDefenseLayerPage"),
);
const XOHorizonPage = lazy(() => import("./pages/XOHorizonPage"));
const XOContinuityReplayPage = lazy(
  () => import("./pages/XOContinuityReplayPage"),
);
const VerifiedCrossPostPage = lazy(
  () => import("./pages/VerifiedCrossPostPage"),
);
const CanonicallsLandingPage = lazy(
  () => import("./pages/CanonicallsLandingPage"),
);
const CanonicallsCanonicalizePage = lazy(
  () => import("./pages/CanonicallsCanonicalizePage"),
);
const CanonicallsInspectPage = lazy(
  () => import("./pages/CanonicallsInspectPage"),
);
const CanonicallsReplayPage = lazy(
  () => import("./pages/CanonicallsReplayPage"),
);
const CanonicallsAetherPage = lazy(
  () => import("./pages/CanonicallsAetherPage"),
);
const XOCasinoLanding = lazy(() => import("./pages/XOCasinoLanding"));
const TrustLoopPage = lazy(() => import("./pages/TrustLoopPage"));
const TrustLoopCardPage = lazy(() => import("./pages/TrustLoopCardPage"));
const XODemoLinkPackPage = lazy(() => import("./pages/XODemoLinkPackPage"));
const XOConstellationPage = lazy(() => import("./pages/XOConstellationPage"));
const XOArtifactPage = lazy(() => import("./pages/XOArtifactPage"));
const SignPage = lazy(() => import("./pages/sign"));
const XOInputPage = lazy(() => import("./pages/XOInputPage"));
const ConversationRecordPage = lazy(
  () => import("./pages/ConversationRecordPage"),
);

const EighthSealDrop = lazy(() => import("./drops/eighth_seal/Drop"));
const TraitExplorer = lazy(() => import("./pages/TraitExplorer"));
const ThreeGlowDemo = lazy(() => import("./pages/ThreeGlowDemo"));
const VaultDashboard = lazy(() =>
  import("./vault/VaultDashboard").then((m) => ({ default: m.VaultDashboard })),
);
const DigestViewer = lazy(() => import("./pages/DigestViewer"));
const XOWatchHome = lazy(() => import("./pages/XOWatchHome"));
const CanonicalMessageBottle = lazy(
  () => import("./pages/CanonicalMessageBottle"),
);
const LedgerViewer = lazy(() => import("./pages/LedgerViewer"));
const LedgerRecordPage = lazy(() => import("./pages/LedgerRecordPage"));
const DropsIndex = lazy(() => import("./pages/DropsIndex"));
const OperatorPage = lazy(() => import("./operator/OperatorPage"));
const PulsePage = lazy(() => import("./pulse/PulsePage"));
const CockpitPage = lazy(() => import("./cockpit/CockpitPage"));
const EventBusDashboard = lazy(() => import("./pages/EventBusDashboard"));
const LiquidityRadar = lazy(() => import("./components/LiquidityRadar"));

function InkColumnSuspense({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Suspense
      fallback={
        <div
          style={{
            flex: 1,
            minHeight: "55vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#1E1B16",
          }}
        >
          <XOPublicQuietLoading label={label} />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

function DevelopmentPreviewRoute({
  path,
  children,
}: {
  path: string;
  children: React.ReactNode;
}) {
  const destination = developmentPreviewDestination(
    path,
    !import.meta.env.PROD,
  );
  if (destination !== path) return <Navigate to={destination} replace />;
  return children;
}

function CallAwarePublicRoot() {
  const hostname =
    typeof globalThis !== "undefined" && "location" in globalThis
      ? (globalThis as unknown as { location: { hostname: string } }).location
          .hostname
      : "";
  if (isCallDedicatedProductRootHost(hostname)) {
    return (
      <XOPublicShellWithSuspense loadingLabel="Loading XO Call…">
        <CallLanding />
      </XOPublicShellWithSuspense>
    );
  }
  return <PublicRootLanding />;
}

/**
 * Branded `/` landing: apex hosts (`BRANDED_DOMAINS`) and planned `*.xo.watch` subdomains
 * (`XO_WATCH_FUTURE_SUBDOMAINS`). Surface family / identity roles: `XO_SURFACE_FAMILY` in
 * `lib/branded_domain_surface.ts`.
 */
function PublicRootLanding() {
  const location = useLocation();
  const hostname =
    typeof globalThis !== "undefined" && "location" in globalThis
      ? (globalThis as unknown as { location: { hostname: string } }).location
          .hostname
      : "";
  const rootParams = new URLSearchParams(location.search);
  if (isXOCenterHost(hostname)) {
    return (
      <XOPublicShellWithSuspense loadingLabel="Loading XO Center…" fullBleed>
        <XOCenterPage />
      </XOPublicShellWithSuspense>
    );
  }
  if (isCanonicallsHost(hostname)) {
    return (
      <CanonicallsRouteGate loadingLabel="Loading…">
        <CanonicallsLandingPage />
      </CanonicallsRouteGate>
    );
  }
  const rootLedgerRecordId = rootParams.get("ledger_record_id")?.trim();
  if (rootLedgerRecordId) {
    const highlightVerify =
      rootParams.get("highlight") === "verify" || location.hash === "#verify";
    const target = `/ledger/record/${encodeURIComponent(rootLedgerRecordId)}${highlightVerify ? "?highlight=verify" : ""}`;
    return <Navigate to={target} replace />;
  }
  const branded = resolveBrandedDomainSurface(hostname);
  if (branded && branded.rootPolicy.mode === "redirect") {
    const target = resolveBrandedRedirectTarget(
      branded.rootPolicy,
      branded.resolvedHubHref,
    );
    if (target.kind === "external") {
      return (
        <XOPublicShellWithSuspense loadingLabel="Continuing…">
          <BrandedDomainExternalRedirect url={target.url} />
        </XOPublicShellWithSuspense>
      );
    }
    return <Navigate to={target.path} replace />;
  }
  if (branded?.id === "watch") {
    return (
      <XOPublicShellWithSuspense loadingLabel="Loading…">
        <XOWatchHome />
      </XOPublicShellWithSuspense>
    );
  }
  if (branded?.id === "pulse") {
    return (
      <XOPublicShellWithSuspense loadingLabel="Loading pulse…">
        <PulsePage />
      </XOPublicShellWithSuspense>
    );
  }
  if (branded?.id === "digest") {
    return (
      <XOPublicShellWithSuspense loadingLabel="Loading digest…">
        <DigestViewer />
      </XOPublicShellWithSuspense>
    );
  }
  if (branded?.id === "ledger") {
    return (
      <XOPublicShellWithSuspense loadingLabel="Loading ledger…">
        <LedgerViewer />
      </XOPublicShellWithSuspense>
    );
  }
  if (branded?.id === "traits") {
    return (
      <XOPublicShellWithSuspense loadingLabel="Loading traits…">
        <TraitExplorer />
      </XOPublicShellWithSuspense>
    );
  }
  if (branded?.id === "bridge") {
    return (
      <XOPublicShellWithSuspense loadingLabel="Loading bridge…">
        <BridgeSurface />
      </XOPublicShellWithSuspense>
    );
  }
  if (branded?.id === "seals") {
    // Provisional characters root; canonical path = XO_SURFACE_FAMILY.seals.hubRoute (`/drops/eighth_seal`).
    return <Navigate to="/drops/eighth_seal" replace />;
  }
  if (branded?.id === "moments") {
    return (
      <XOPublicShellWithSuspense
        loadingLabel="Loading moments…"
        hideTraversalRail
        hideYouAreHere
      >
        <Moments21XoLanding />
      </XOPublicShellWithSuspense>
    );
  }
  if (branded?.id === "vault") {
    return (
      <XOPublicShellWithSuspense loadingLabel="Loading vault…">
        <VaultSurface />
      </XOPublicShellWithSuspense>
    );
  }
  const experience = resolveRootLandingExperience(hostname);
  if (experience === "constellation") {
    return <Navigate to="/constellation" replace />;
  }
  if (experience === "pitch") {
    return (
      <XOPublicShellWithSuspense loadingLabel="Loading pitch…" fullBleed>
        <XOPitchPage />
      </XOPublicShellWithSuspense>
    );
  }
  if (experience === "secure_execution") {
    return (
      <XOPublicShellWithSuspense loadingLabel="Loading…">
        <AetherSecureLanding />
      </XOPublicShellWithSuspense>
    );
  }
  if (experience === "casino_continuum") {
    return (
      <XOPublicShellWithSuspense loadingLabel="Loading continuum…" fullBleed>
        <XOCasinoLanding />
      </XOPublicShellWithSuspense>
    );
  }
  return (
    <XOPublicShellWithSuspense loadingLabel="Loading…">
      <XOWatchHome />
    </XOPublicShellWithSuspense>
  );
}

function XOCenterHubRoute() {
  const hostname =
    typeof globalThis !== "undefined" && "location" in globalThis
      ? (globalThis as unknown as { location: { hostname: string } }).location
          .hostname
      : "";
  if (isXOCenterHost(hostname)) {
    return <Navigate to="/" replace />;
  }
  return (
    <XOPublicShellWithSuspense loadingLabel="Loading XO Center…" fullBleed>
      <XOCenterPage />
    </XOPublicShellWithSuspense>
  );
}

function DemoAwareMaterialRoute() {
  const { search } = useLocation();
  const isDemoMode =
    new URLSearchParams(search).get("demo") === "1" ||
    new URLSearchParams(search).get("demo") === "true";
  const body = (
    <XoProofRouteErrorBoundary>
      <XOProofMaterialPage />
    </XoProofRouteErrorBoundary>
  );
  if (isDemoMode) {
    return (
      <XODemoSurfaceShell loadingLabel="Loading material…">
        {body}
      </XODemoSurfaceShell>
    );
  }
  return <InkColumnSuspense label="Loading proof…">{body}</InkColumnSuspense>;
}

function RegistrarHostRoute({ children }: { children: React.ReactNode }) {
  const hostname =
    typeof globalThis !== "undefined" && "location" in globalThis
      ? (globalThis as unknown as { location: { hostname: string } }).location
          .hostname
      : "";
  if (!isRegistrarAllowedHost(hostname)) {
    return <Navigate to={registrarRedirectForHost(hostname)} replace />;
  }
  return <>{children}</>;
}

function AppShell() {
  const { pathname, search, key: locationKey } = useLocation();
  const hostname =
    typeof globalThis !== "undefined" && "location" in globalThis
      ? (globalThis as unknown as { location: { hostname: string } }).location
          .hostname
      : "";
  if (isSharedContinuityRoomProductHost(hostname)) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          width: "100%",
          backgroundColor: "#1E1B16",
          color: "#F6F0E6",
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#1E1B16",
          }}
        >
          <SharedContinuityRoomHostApp />
        </div>
      </div>
    );
  }
  const mb2Focus = isMessageBottleV2Path(pathname);
  const canonicallsChrome = shouldUseCanonicallsShell(hostname, pathname);
  const publicEx =
    isPublicCinematicShellPath(pathname, hostname) ||
    canonicallsChrome ||
    isXOCenterVisitorShellPath(hostname, pathname);
  const demoSurfaceActive =
    new URLSearchParams(search).get("demo") === "1" &&
    isXoDemoSurfacePath(pathname);
  return (
    <div
      className="xo-app-shell"
      style={{
        display: "flex",
        alignItems: "stretch",
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        width: "100%",
        backgroundColor: "#1E1B16",
        color: "#F6F0E6",
      }}
    >
      {!publicEx && !demoSurfaceActive ? <Sidebar /> : null}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          padding: publicEx ? 0 : mb2Focus ? "0.35rem 0.5rem" : "1rem",
          display: "flex",
          flexDirection: "column",
          backgroundColor: publicEx ? "#1E1B16" : "transparent",
        }}
      >
        {/*
         * Route map (single app today). Deployment intent:
         * - xoflip.com → proof / flip / authenticity surfaces (extend with dedicated verifier routes as they ship)
         * - constellation.bond → traits, map, progression, digest / inbox-style views
         * See also: navigation/surfaceDomains.ts
         * - `/` picks hub vs pitch vs Aether secure entry from hostname (`lib/host_root_landing.ts`).
         *
         * Cinematic public surfaces: /, /message-bottle, /pitch, /reserve, /xonvergence, /drops/signals, /signal-pass — no sidebar, ink base + `XOPublicShell`.
         */}
        <HostSurfaceAutoRoute />
        <Routes>
          {/*
           * xo.watch data origins (see lib/api_origin.ts header):
           * / → XOWatchHome hub strip: Proof + governance live-data health (diagnostics only).
           * /digest → DigestViewer: Proof API GET /api/digest only (VITE_API_* / Proof Connect).
           * /pulse → PulsePage: pulse host via getPulseApiOriginCandidates + probe (vault-ops before governance); signal access uses same host.
           * /drops/message_bottle_v2 → constellation: getConstellationApiOrigin (Proof; dev localhost fallback).
           */}
          <Route path="/" element={<CallAwarePublicRoot />} />
          <Route
            path="/message-bottle"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading…">
                <CanonicalMessageBottle />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/message-bottle/record"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading record…">
                <LedgerViewer />
              </XOPublicShellWithSuspense>
            }
          />
          {/* Continuity layer: replayable — canonical witness record (not the living vessel). */}
          <Route
            path="/records/message-bottle-v2"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading continuity record…">
                <LedgerViewer />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/ledger"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading ledger…">
                <LedgerViewer />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/ledger/record/:ledger_record_id"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading ledger record…">
                <LedgerRecordPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/vault"
            element={
              <InkColumnSuspense label="Loading vault…">
                <VaultDashboard />
              </InkColumnSuspense>
            }
          />
          <Route
            path="/conversation"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading Conversation Record…">
                <ConversationRecordPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/input"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading XO Input…">
                <XOInputPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/drops"
            element={
              <InkColumnSuspense label="Loading drops…">
                <DropsIndex />
              </InkColumnSuspense>
            }
          />
          <Route
            path="/operator"
            element={
              <InkColumnSuspense label="Loading operator…">
                <OperatorPage key={locationKey} />
              </InkColumnSuspense>
            }
          />
          <Route
            path="/operator/execution-logbook"
            element={
              <InkColumnSuspense label="Loading operator…">
                <OperatorPage key={locationKey} />
              </InkColumnSuspense>
            }
          />
          <Route
            path="/foundry/operator/care-review"
            element={
              <InkColumnSuspense label="Opening the Care Review Desk…">
                <FoundryCareReviewDeskPage />
              </InkColumnSuspense>
            }
          />
          <Route
            path="/foundry/operator/secure-element"
            element={
              <InkColumnSuspense label="Opening the Secure Element Bench…">
                <FoundrySecureElementProvisioningPage />
              </InkColumnSuspense>
            }
          />
          <Route
            path="/foundry/operator/nfc-rehearsal"
            element={<InkColumnSuspense label="Opening the NFC Rehearsal Console…"><FoundryNfcRehearsalConsolePage /></InkColumnSuspense>}
          />
          <Route
            path="/foundry/operator/nfc-pilot-matrix"
            element={<InkColumnSuspense label="Opening the NFC Pilot Matrix…"><FoundryNfcPilotMatrixPage /></InkColumnSuspense>}
          />
          <Route
            path="/foundry/operator/physical-pilot"
            element={<InkColumnSuspense label="Opening the Physical Pilot…"><FoundryPhysicalPilotRunPage /></InkColumnSuspense>}
          />
          <Route
            path="/foundry/operator/pilot-evidence-capsule"
            element={<InkColumnSuspense label="Opening the Pilot Evidence Capsule…"><FoundryPilotEvidenceCapsulePage /></InkColumnSuspense>}
          />
          <Route
            path="/foundry/operator/pilot-promotion-review"
            element={<InkColumnSuspense label="Opening the Pilot Promotion Review…"><FoundryPilotPromotionReviewPage /></InkColumnSuspense>}
          />
          <Route
            path="/harbor/care/:requestDigest"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Following the care note…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <HarborCareReceiptPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/care-proof/:reviewDigest"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Verifying the care proof…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <HarborCareProofPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/care-closure/:closureDigest"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Following the closure seal…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <HarborCareClosurePage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/care-journey/:receiptDigest"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Following the care chain…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <HarborCareJourneyPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/keeper-authority/:rotationDigest"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Following the authority seal…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <HarborKeeperAuthorityProofPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/keeper-lineage-proof/:headDigest"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Following the Keeper lineage…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <HarborKeeperLineageProofPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/keeper-handoff/:handoffDigest"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Opening the passage seal…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <HarborKeeperHandoffProofPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/artifact/:artifactRef"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Waking the artifact story…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <HarborPermanentArtifactPortalPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/artifact/:artifactRef/passport"
            element={
              <XOPublicShellWithSuspense loadingLabel="Opening the living passport…" hideTraversalRail hideYouAreHere fullBleed>
                <HarborLivingArtifactPassportPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/artifact/:artifactRef/presence"
            element={
              <XOPublicShellWithSuspense loadingLabel="Preparing a fresh presence challenge…" hideTraversalRail hideYouAreHere fullBleed>
                <HarborFreshPresenceCeremonyPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/companion/presence"
            element={
              <XOPublicShellWithSuspense loadingLabel="Receiving the object’s question…" hideTraversalRail hideYouAreHere fullBleed>
                <CompanionPresenceReceiverPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/mark-binding/:manifestDigest/:bindingDigest"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Reading the Foundry mark…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <HarborArtifactMarkBindingPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/tap-presence/:receiptDigest"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Reading the physical trace…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <HarborSecureTapPresencePage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/secure-element-lineage/:manifestDigest"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Following the technical lineage…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <HarborSecureElementLineagePage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/trust/physical-artifacts"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Opening the physical trust handbook…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <XOPhysicalTrustHandbookPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route path="/call-room" element={<Navigate to="/call" replace />} />
          <Route
            path="/call-room-audio-demo"
            element={<Navigate to="/call" replace />}
          />
          <Route
            path="/call/room-audio-demo"
            element={<Navigate to="/call/dev" replace />}
          />
          <Route
            path="/call/simple"
            element={<Navigate to="/call" replace />}
          />
          <Route path="/call/user" element={<Navigate to="/call" replace />} />
          <Route
            path="/call/operator"
            element={<Navigate to="/call/dev" replace />}
          />
          <Route
            path="/call/dev"
            element={
              <XODemoSurfaceShell loadingLabel="Loading call…">
                <CallRoomAudioDemo />
              </XODemoSurfaceShell>
            }
          />
          <Route
            path="/call/landing"
            element={
              <XODemoSurfaceShell loadingLabel="Loading XO Call…">
                <CallLanding />
              </XODemoSurfaceShell>
            }
          />
          <Route
            path="/call"
            element={
              <CallRouteShell loadingLabel="Loading XO Call…">
                <CallRouteErrorBoundary>
                  <CallV1 />
                </CallRouteErrorBoundary>
              </CallRouteShell>
            }
          />

          <Route
            path="/call-continuity-demo"
            element={
              <XODemoSurfaceShell loadingLabel="Loading continuity demo…">
                <XOCallContinuityDemoLaunch />
              </XODemoSurfaceShell>
            }
          />

          <Route
            path="/pulse"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading pulse…">
                <PulsePage key={locationKey} />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/cockpit"
            element={
              <InkColumnSuspense label="Loading cockpit…">
                <CockpitPage key={locationKey} />
              </InkColumnSuspense>
            }
          />
          <Route
            path="/signal-pass"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading surface…">
                <SignalPassPage key={locationKey} />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/artifacts/:module"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading artifact..."
                fullBleed
              >
                <XOArtifactPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/constellation"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading constellation..."
                fullBleed
              >
                <XOConstellationPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/constellation/:module"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading constellation..."
                fullBleed
              >
                <XOConstellationPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/trust-continuum"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading trust continuum…">
                <TrustContinuumFrame />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/primer/what-xo-is"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading primer…"
                fullBleed
              >
                <XOPrimerWhatXoIsPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/primer/why-systems-fail"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading primer…"
                fullBleed
              >
                <XOPrimerWhySystemsFailPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/primer/trust-continuum"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading primer…"
                fullBleed
              >
                <XOPrimerTrustContinuumPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/primer/continuity-surfaces"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading primer…"
                fullBleed
              >
                <XOPrimerContinuitySurfacesPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/primer/defense-layer"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading primer…"
                fullBleed
              >
                <XOPrimerDefenseLayerPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/primer/live-demo"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading live demo…"
                fullBleed
              >
                <PrimerLiveDemo />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/xo-center/manifest"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading operator mounts…"
                fullBleed
              >
                <XOCenterManifestPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route path="/xo-center" element={<XOCenterHubRoute />} />
          <Route
            path="/primer/control-room"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading control room…"
                fullBleed
              >
                <PrimerControlRoom />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/primer"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading primer…"
                fullBleed
              >
                <XOPrimerPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/horizon"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading horizon…"
                fullBleed
              >
                <XOHorizonPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/cross-post"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading verified cross post…"
                fullBleed
              >
                <VerifiedCrossPostPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/canonicalize"
            element={
              <CanonicallsRouteGate loadingLabel="Loading…">
                <CanonicallsCanonicalizePage />
              </CanonicallsRouteGate>
            }
          />
          <Route
            path="/inspect"
            element={
              <CanonicallsRouteGate loadingLabel="Loading…">
                <CanonicallsInspectPage />
              </CanonicallsRouteGate>
            }
          />
          <Route
            path="/replay"
            element={
              <CanonicallsRouteGate loadingLabel="Loading…">
                <CanonicallsReplayPage />
              </CanonicallsRouteGate>
            }
          />
          <Route
            path="/aether"
            element={
              <CanonicallsRouteGate loadingLabel="Loading…">
                <CanonicallsAetherPage />
              </CanonicallsRouteGate>
            }
          />
          <Route
            path="/continuity-replay"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading continuity replay…"
                fullBleed
              >
                <XOContinuityReplayPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/continuity-scene"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading continuity scene…"
                fullBleed
              >
                <XOContinuityScene />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/continuity-session"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading continuity session…"
                fullBleed
              >
                <XOContinuitySession />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/continuity-room"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading shared continuity room…"
                fullBleed
              >
                <XOSharedContinuityRoom />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/casino"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading continuum…"
                fullBleed
              >
                <XOCasinoLanding />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/trust-loop/card"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading trust loop card…"
                fullBleed
              >
                <TrustLoopCardPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/trust-loop"
            element={
              <XODemoSurfaceShell loadingLabel="Loading trust loop…">
                <TrustLoopPage />
              </XODemoSurfaceShell>
            }
          />
          <Route
            path="/demo"
            element={
              <XODemoSurfaceShell loadingLabel="Loading XO demo…">
                <XODemoLinkPackPage />
              </XODemoSurfaceShell>
            }
          />
          <Route
            path="/dashboard"
            element={
              <InkColumnSuspense label="Loading dashboard…">
                <EventBusDashboard />
              </InkColumnSuspense>
            }
          />
          <Route path="/xo" element={<Navigate to="/mission" replace />} />
          <Route
            path="/mission"
            element={
              <InkColumnSuspense label="Loading Mission Control…">
                <XOMissionControl />
              </InkColumnSuspense>
            }
          />
          <Route
            path="/xo-proof/artifact/*"
            element={
              <InkColumnSuspense label="Loading proof artifact…">
                <XoProofRouteErrorBoundary>
                  <XOProofArtifactPage />
                </XoProofRouteErrorBoundary>
              </InkColumnSuspense>
            }
          />
          <Route
            path="/xo-proof/aether/:aetherId"
            element={
              <InkColumnSuspense label="Loading proof…">
                <XoProofRouteErrorBoundary>
                  <XOProofAetherPage />
                </XoProofRouteErrorBoundary>
              </InkColumnSuspense>
            }
          />
          <Route
            path="/record/call"
            element={
              <XODemoSurfaceShell loadingLabel="Loading record…">
                <XoProofRouteErrorBoundary>
                  <XOProofCallPage />
                </XoProofRouteErrorBoundary>
              </XODemoSurfaceShell>
            }
          />
          <Route
            path="/record/call/:call_id"
            element={
              <XODemoSurfaceShell loadingLabel="Loading record…">
                <XoProofRouteErrorBoundary>
                  <XOProofCallPage />
                </XoProofRouteErrorBoundary>
              </XODemoSurfaceShell>
            }
          />
          <Route
            path="/xo-proof/call/:callId"
            element={
              <InkColumnSuspense label="Loading proof…">
                <XoProofRouteErrorBoundary>
                  <XOProofCallPage />
                </XoProofRouteErrorBoundary>
              </InkColumnSuspense>
            }
          />
          <Route
            path="/xo-proof/material/:materialId"
            element={<DemoAwareMaterialRoute />}
          />
          <Route
            path="/xo-proof"
            element={
              <InkColumnSuspense label="Loading proof hub…">
                <XoProofRouteErrorBoundary>
                  <XOProofPage />
                </XoProofRouteErrorBoundary>
              </InkColumnSuspense>
            }
          />
          <Route
            path="/reserve"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading surface…">
                <XOReserve />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/xonvergence"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading surface…">
                <XOnvergence />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/terminal"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading surface…">
                <XOTerminal />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/convergence"
            element={<Navigate to="/xonvergence" replace />}
          />
          <Route
            path="/xo-convergence"
            element={<Navigate to="/xonvergence" replace />}
          />
          <Route
            path="/liquidity-radar"
            element={
              <InkColumnSuspense label="Loading liquidity radar…">
                <LiquidityRadar />
              </InkColumnSuspense>
            }
          />
          <Route
            path="/drops/signals"
            element={
              <XOPublicShellWithSuspense loadingLabel="Preparing signal view…">
                <SignalsDropPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/drops/eighth_seal"
            element={
              <InkColumnSuspense label="Loading drop…">
                <EighthSealDrop />
              </InkColumnSuspense>
            }
          />
          <Route
            path="/drops/message_bottle_v2"
            element={<Navigate to="/message-bottle" replace />}
          />

          <Route path="/drops/eighth_seal/claim" element={<Claim />} />
          <Route
            path="/traits"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading traits…">
                <TraitExplorer />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/three-glow-demo"
            element={
              <InkColumnSuspense label="Loading demo…">
                <ThreeGlowDemo dropId="eighth_seal_3d" />
              </InkColumnSuspense>
            }
          />
          <Route
            path="/dev/proposal-lifecycle"
            element={
              <DevelopmentPreviewRoute path="/dev/proposal-lifecycle">
                <XOPublicShellWithSuspense loadingLabel="Loading proposal lifecycle preview…">
                  <ProposalLifecyclePreview />
                </XOPublicShellWithSuspense>
              </DevelopmentPreviewRoute>
            }
          />
          <Route
            path="/digest"
            element={
              <InkColumnSuspense label="Loading digest…">
                <DigestViewer />
              </InkColumnSuspense>
            }
          />
          <Route
            path="/pitch"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading pitch…"
                fullBleed
              >
                <XOPitchPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/card/:storyId"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading Anchor Seal Card…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <MomentsAnchorSealCardPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/moments/arrival/:linkId"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading harbor arrival…"
                hideTraversalRail
                hideYouAreHere
              >
                <MomentsHarborArrivalPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor-walk"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading harbor walk…"
                hideTraversalRail
                hideYouAreHere
              >
                <HarborWalkPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/provenance/:manifestDigest"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Following the thread…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <HarborProvenancePage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/portal/:manifestDigest/mark"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Preparing the mark…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <HarborPhysicalPortalMarkPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/passage/:acceptanceToken"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Opening the passage…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <HarborPassageCeremonyPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/foundry/collections/:collectionRef"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Reading the edition ledger…"
                hideTraversalRail
                hideYouAreHere
                fullBleed
              >
                <FoundryCollectionScarcityPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor/:districtSlug"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading harbor district…"
                hideTraversalRail
                hideYouAreHere
              >
                <HarborDistrictPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/harbor-seal-room"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading seal room…"
                hideTraversalRail
                hideYouAreHere
              >
                <HarborSealRoomPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/moments"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading moments…"
                hideTraversalRail
                hideYouAreHere
              >
                <Moments21XoLanding />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/my-harbor"
            element={
              <XOPublicShellWithSuspense
                loadingLabel="Loading My Harbor…"
                hideTraversalRail
                hideYouAreHere
              >
                <MyHarborPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/aether/intent/:routeId"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading…">
                <AetherIntentPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/aether/:handle/route"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading…">
                <AetherRoutePage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/aether/:handle"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading…">
                <AetherHandlePage />
              </XOPublicShellWithSuspense>
            }
          />

          <Route
            path="/registrar"
            element={
              <RegistrarHostRoute>
                <XOPublicShellWithSuspense loadingLabel="Loading…">
                  <AetherRegistrarListPage />
                </XOPublicShellWithSuspense>
              </RegistrarHostRoute>
            }
          />
          <Route
            path="/registrar/:handle"
            element={
              <RegistrarHostRoute>
                <XOPublicShellWithSuspense loadingLabel="Loading…">
                  <AetherRegistrarEditPage />
                </XOPublicShellWithSuspense>
              </RegistrarHostRoute>
            }
          />
          <Route
            path="/proof/:proofId"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading…">
                <ProofViewerPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/handoff/:rail"
            element={
              <XOPublicShellWithSuspense loadingLabel="Loading…">
                <HandoffPage />
              </XOPublicShellWithSuspense>
            }
          />
          <Route
            path="/sign"
            element={
              <InkColumnSuspense label="Loading sign…">
                <SignPage />
              </InkColumnSuspense>
            }
          />
          <Route path="*" element={<XOCatchAllRoute />} />
        </Routes>
      </div>
    </div>
  );
}

const App = () => (
  <BrowserRouter>
    <AppShell />
  </BrowserRouter>
);

const container = document.getElementById("root");
if (!container) {
  document.body.insertAdjacentHTML(
    "afterbegin",
    '<div id="root-missing" style="padding:1.25rem;font-family:system-ui,sans-serif;background:#1E1B16;color:#F6F0E6;min-height:100vh;box-sizing:border-box">#root not found in index.html</div>',
  );
} else {
  const root = createRoot(container);
  root.render(
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>,
  );
}
